import {Injectable} from '@angular/core';
import {ToastService} from "./toast.service";
import {addDays, addHours, addMonths, addWeeks, isAfter, startOfDay} from "date-fns";
import JSConfetti from "js-confetti";
import {cloneDeep, set} from "lodash";
import {AuthService} from "../auth.service";
import {ComputationReferenceInterface} from "../../interfaces/computation/computation-reference.interface";
import {ComputationCategoryType} from "../../interfaces/computation/computation-category.type";
import {computations} from "../../../../assets/data/computations";
import {ScheduleInterface} from "../../interfaces/extractor/schedule.interface";
import {BodyConstantsInterface} from "../../interfaces/body-constants.interface";
import {ActionMappingInterface} from "../../interfaces/destination/action-mapping.interface";
import {IntegrationTypes} from "../../interfaces/integrations/integration-types.type";
import {standardDestinations} from "../../../../assets/data/destinations";
import {builtInExtractions, builtInSources} from "../../../../assets/data/built-in-sources";

@Injectable({
  providedIn: 'root'
})
export class HelperService {
  uid: string
  email: string
  constructor(
    private toastSvc: ToastService,
    private authSvc: AuthService
  ) {
    this.authSvc.user$.subscribe((user) => {
      this.uid = user?.uid ?? ''
      this.email = user?.email ?? ''
    })
  }

  copyText(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.toastSvc.success('Copied to your clipboard')
    }, function(err) {
      console.error('Async: Could not copy text: ', err);
    });
  }

  /** Board URL with a card deep-linked — see the ?card= handling in BoardComponent. */
  copyCardLink(cardId: string) {
    this.copyText(`${location.origin}${location.pathname}?card=${cardId}`)
  }

  generateId(length: number = 8): string {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for ( let i = 0; i < length; i++ ) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  scrollTo(elementId: string): boolean {
    const element = document.getElementById(elementId);
    if (!element) {
      return false;
    }
    const y = element.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({top: y, behavior: 'smooth'});
    return true;
  }

  scrollIntoView(elementId: string): boolean {
    const element = document.getElementById(elementId);
    if (!element) {
      return false;
    }
    document.getElementById("btn-add-property")?.scrollIntoView(
      {
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      }
    )
    return true;
  }

  setMetadata(object: any): any {
    if (object.createdAt) { // Object already exists, set update data
      object.lastUpdate = new Date().getTime()
      object.lastUpdateBy = this.email
    } else { // New object
      object.createdAt = new Date().getTime()
      object.createdBy = this.email
    }
    return object
  }

  capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  groupComputations(computations: ComputationReferenceInterface[]) {
    const result: {
      group: ComputationCategoryType
      computations: ComputationReferenceInterface[]
    }[] = []
    computations.forEach((computation) => {
      const group = result.find(x => x.group === computation.group)
      if (!group) {
        result.push({
          group: computation.group,
          computations: [computation]
        })
      } else {
        group.computations.push(computation)
      }
    })
    console.log('Result', result)
    return result
  }

  listGroups() {
    const result: ComputationCategoryType[] = []
    computations.forEach((computation) => {
      const group = result.find(x => x === computation.group)
      if (!group) {
        result.push(computation.group)
      }
    })
    return result
  }

  _deleteArrayElement(array: string[], element: string): string[] {
    const index = array.findIndex(x => x === element)
    if (index !== -1) {
      array.splice(index, 1)
    }
    return array
  }

  _addArrayElement(array: string[], element: string): string[] {
    const index = array.findIndex(x => x === element)
    if (index === -1) {
      array.push(element)
    }
    return array
  }

  // isEqual(obj1: any, obj2: any): boolean {
  //   if (obj1 === undefined || obj2 === undefined) {
  //     return true
  //   }
  //   const keys1 = Object.keys(obj1);
  //   const keys2 = Object.keys(obj2);
  //   console.log('Obj 1', obj1)
  //   console.log('Obj 2', obj2)
  //
  //   // tslint:disable-next-line:triple-equals
  //   if (keys1.length != keys2.length) {
  //     return false;
  //   }
  //   for (const key of keys1) {
  //     const bothAreObjects = typeof (obj1[key]) === `object` && typeof (obj2[key]) === `object` && obj1[key] !== null && obj2[key] !== null;
  //     if ((!bothAreObjects && (obj1[key] !== obj2[key])) || (bothAreObjects && !this.isEqual(obj1[key], obj2[key]))) {
  //       return false;
  //     }
  //   }
  //   return true;
  // }

  _isExpired(expirationDate?: number) {
    return isAfter(new Date(), new Date(expirationDate ?? 0))
  }

  toJsonPropertyName(str: string): string {
    // Replace non-alphanumeric characters with underscores, one per run, none at the end: "Revenue (USD)" → "Revenue_USD"
    let result = str.replace(/[^a-zA-Z0-9_]/g, '_').replace(/_+/g, '_').replace(/_$/, '');

    // If the first character is a number, prepend an underscore
    if (result.match(/^[0-9]/)) {
      result = '_' + result;
    }

    return result;
  }

  getAllKeyPaths(obj: any, parentKey: string = '', onlyLists: boolean = false): string[] {
    let keyPaths: string[] = [];

    if (!obj) {
      keyPaths.push(parentKey);
      return keyPaths;
    }

    const keys = Object.keys(obj);

    for (const key of keys) {
      const currentKeyPath = parentKey ? `${parentKey}.${key}` : key;

      if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
        const subKeyPaths = this.getAllKeyPaths(obj[key], currentKeyPath, onlyLists);
        keyPaths = keyPaths.concat(subKeyPaths);
      } else if (Array.isArray(obj[key])) {
        if (onlyLists) {
          keyPaths.push(currentKeyPath);
        } else {
          keyPaths.push(currentKeyPath);
          const subKeyPaths = this.getAllKeyPaths(obj[key], currentKeyPath, onlyLists);
          keyPaths = keyPaths.concat(subKeyPaths);
        }
      } else if (!onlyLists) {
        keyPaths.push(currentKeyPath);
      }
    }

    return keyPaths;
  }


  copyElement(element: any, fieldsToUpdate: string[]): any {
    const copy = cloneDeep(element)
    fieldsToUpdate.forEach((field) => {
      copy[field] += '_copy'
    })
    copy['id'] = this.generateId()
    return copy
  }

  canBeUnwrapped(obj: any, isListProcessing = false): boolean {
    console.log('Unwrap object', obj)
    const keys = Object.keys(obj);
    console.log('Unwrap object keys', keys)
    return keys.length === 1 && (typeof obj[keys[0]] === 'object' || isListProcessing);
  }

  unwrapObject(obj: Record<string, any>, failSafe = false): Record<string, any> | string | null {
    const keys = Object.keys(obj);
    if (keys.length === 1 && typeof obj[keys[0]] === 'object') {
    // if (keys.length === 1) {
      return obj[keys[0]];
    }
    return (failSafe) ? obj : null;
  }

  replaceUndefinedByString(obj: any): any {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.replaceUndefinedByString(item));
    }

    const result: { [key: string]: any } = {};
    for (const key in obj) {
      if (obj[key] === undefined) {
        result[key] = 'undefined';
      } else {
        result[key] = this.replaceUndefinedByString(obj[key]);
      }
    }

    return result;
  }

  flattenJsonObject(obj: any, parentKey?: string): any {
    let result: any = {};

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = parentKey ? `${parentKey}.${key}` : key;

        if (typeof obj[key] === 'object' && obj[key] !== null) {
          // Recursively flatten nested objects
          result = { ...result, ...this.flattenJsonObject(obj[key], newKey) };
        } else {
          result[newKey] = obj[key];
        }
      }
    }

    return result;
  }


  unifyJsonObjects(objects: any[]): any[] {
    // Get all unique keys from all unified 'data' objects
    const allKeys = Array.from(new Set(objects.flatMap(obj => Object.keys(obj.data))));

    // Iterate through each object and add missing keys with null values for the unified 'data' property
    const unifiedObjects = objects.map(obj => {
      const newData: any = { ...obj.data }; // Create a new object to avoid modifying the original 'data'
      allKeys.forEach(key => {
        if (!(key in newData)) {
          newData[key] = null; // Assign null if key is missing
        }
      });
      return { ...obj, data: newData };
    });

    return unifiedObjects;
  }

  sanitizeFirebaseDoc(data: any): any {
    data = this.replaceUndefinedWithNull(data)
    data = this.replaceArraysOfArrays(data)
    return data
  }

  replaceUndefinedWithNull(obj: any): any {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.replaceUndefinedWithNull(item));
    }

    const result: { [key: string]: any } = {};
    for (const key in obj) {
      if (obj[key] === undefined) {
        result[key] = null;
      } else {
        result[key] = this.replaceUndefinedWithNull(obj[key]);
      }
    }

    return result;
  }

  replaceArraysOfArrays(obj: any): any {
    if (Array.isArray(obj)) {
      if (obj.some((item) => Array.isArray(item))) {
        return `list:${JSON.stringify(obj)}`;
      }
    }

    if (typeof obj === 'object' && obj !== null) {
      for (const key in obj) {
        obj[key] = this.replaceArraysOfArrays(obj[key]);
      }
    }

    return obj;
  }

  generateBasicAuthHeader(email: string, password: string): string {
    const credentials = `${email}:${password}`;
    const base64Credentials = window.btoa(credentials);
    return `Basic ${base64Credentials}`;
  }

  downloadJson(jsonObject: object, filename: string) {
    const dataStr = JSON.stringify(jsonObject, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', filename);
    linkElement.click();
  }

  /** Rows out to a spreadsheet. `columns` drives both the header and the field order, so the file
   *  matches the table the user was looking at rather than the union of whatever keys happened to
   *  come back — tracked records are user-mapped, so that union drifts from page to page.
   *  A Blob rather than downloadJson's data URI: a full export is megabytes, past what a URI holds.
   *  The BOM is what makes Excel read UTF-8 instead of mangling accents. */
  downloadCsv(rows: Record<string, any>[], columns: { id: string, name: string }[], filename: string) {
    const escape = (value: any) => {
      if (value === undefined || value === null) { return '' }
      const text = typeof value === 'object' ? JSON.stringify(value) : String(value)
      return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
    }
    const csv = [
      columns.map(column => escape(column.name)).join(','),
      ...rows.map(row => columns.map(column => escape(row[column.id])).join(',')),
    ].join('\n')

    const url = URL.createObjectURL(new Blob(['\ufeff' + csv], {type: 'text/csv;charset=utf-8'}))
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', url);
    linkElement.setAttribute('download', filename);
    linkElement.click();
    URL.revokeObjectURL(url)
  }

  async popConfetti(options: object = {}) {
    const confetti = new JSConfetti()
    confetti.addConfetti(options).then()
    setTimeout(() => { confetti.addConfetti(options)}, 300)
    setTimeout(() => { confetti.addConfetti(options)}, 600)
  }

  sortObjectKeys(value: any): any {
    if (Array.isArray(value)) {
      return value.map(item => this.sortObjectKeys(item));
    } else if (value !== null && typeof value === 'object') {
      return Object.keys(value)
        .sort()
        .reduce((result: any, key) => {
          set(result, key, this.sortObjectKeys(value[key]));
          return result;
        }, {});
    } else {
      return value;
    }
  }

  calculateNextActualCronJob(schedule: ScheduleInterface): Date {
    let nextRun = startOfDay(new Date(schedule.startAt));
    nextRun = addHours(nextRun, schedule.startAtHour);
    console.log('First start', nextRun);
    // If the calculated time is in the past, move to the next valid run
    const now = new Date();
    while (nextRun <= now) {
      switch (schedule.intervalUnit) {
        case 'hour':
          nextRun = addHours(nextRun, schedule.interval);
          break;
        case "day":
          nextRun = addDays(nextRun, schedule.interval);
          break;
        case "week":
          nextRun = addWeeks(nextRun, schedule.interval);
          break;
        case "month":
          nextRun = addMonths(nextRun, schedule.interval);
          break;
      }
    }
    console.log('Next run at: ', nextRun.toISOString());
    return nextRun;
  }

  migrateDataFetchMappings(properties: BodyConstantsInterface[]): ActionMappingInterface[] {
    const result = [] as ActionMappingInterface[]
    properties.forEach((property) => {
      result.push({
        computed: property.value ?? '',
        type: property.type ?? 'string',
        mode: property.mode ?? 'constant',
        context: '',
        platformType: property.columnType ?? '',
        matchOn: property.matchOn ?? '',
        platform: ''
      })
    })
    return result
  }

  getBasicMapping(defaultMatchOn = 'value') {
    return {
      computed: '',
      type: 'string',
      mode: 'constant',
      context: '',
      platform: '',
      matchOn: defaultMatchOn
    } as ActionMappingInterface
  }


  // OAuth helper
  randomString(length: number): string {
    const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
    let result = ''
    for (let i = length; i > 0; --i)
      result += chars[Math.floor(Math.random() * chars.length)]
    return result
  }

  // base64UrlEncode(buffer: ArrayBuffer): string {
  //   const bytes = new Uint8Array(buffer)
  //   let binary = ""
  //   for (let i = 0; i < bytes.length; i++) {
  //     binary += String.fromCharCode(bytes[i])
  //   }
  //
  //   return btoa(binary)
  //     // .replace(/\+/g, "_")
  //     // .replace(/\//g, "-")
  //     // .replace(/=+$/, "")
  // }

  async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder()
    const data = encoder.encode(codeVerifier)
    const digest = await crypto.subtle.digest('SHA-256', data)
    const codeVerifierHash = btoa(String.fromCharCode(...new Uint8Array(digest)))
    return codeVerifierHash
      .replace(/=+$/, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_')

  }

  isBuiltInIntegration(type: IntegrationTypes): boolean {
    if (type === 'webhook') { return true }
    return standardDestinations.findIndex(x => x.type === type) !== -1
      || builtInSources.findIndex(x => x.type === type) !== -1
      || builtInExtractions.findIndex(x => x.type === type) !== -1
  }
}
